import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Utility function to extract readable error message
export const getErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // Handle Axios error response
  const detail = error.response?.data?.detail;
  if (detail) {
    if (typeof detail === 'string') return detail;
    if (detail.message) return detail.message;
    if (detail.msg) return detail.msg;
    if (Array.isArray(detail)) {
      // Pydantic validation errors come as array
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    return JSON.stringify(detail);
  }
  
  // Handle regular error object
  if (error.message) return error.message;
  if (typeof error === 'string') return error;
  
  return 'An unknown error occurred';
};

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors + iter317 sliding-session refresh
api.interceptors.response.use(
  (response) => {
    // iter317 — If the backend minted a fresh token (because the current
    // one had < 48h left), swap the stored token so subsequent requests
    // ride the new expiry. This makes the session self-renewing for any
    // user who touches the app at least once every 30 days.
    try {
      const fresh = response?.headers?.['x-refreshed-token'] || response?.headers?.['X-Refreshed-Token'];
      if (fresh && typeof fresh === 'string' && fresh.length > 10) {
        localStorage.setItem('token', fresh);
      }
    } catch (_) { /* ignore — never let refresh break the happy path */ }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect to login if we're on a public page
      const isPublicPage = window.location.pathname === '/properties/new' || 
                           window.location.pathname === '/login';
      if (!isPublicPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Lead Analytics API
export const leadAnalyticsAPI = {
  getComprehensive: () => api.get('/analytics/leads/comprehensive'),
  getFiltered: (filters) => api.post('/analytics/leads/comprehensive', { filters }),
  getDrilldown: (params) => api.get('/analytics/leads/drilldown', { params }),
  postDrilldown: (payload) => api.post('/analytics/leads/drilldown', payload),
  timeComparison: (data) => api.post('/analytics/leads/time-comparison', data),
};

// Property Analytics API
export const propertyAnalyticsAPI = {
  getComprehensive: () => api.get('/analytics/properties/comprehensive'),
  getFiltered: (filters) => api.post('/analytics/properties/comprehensive', { filters }),
  getDrilldown: (params) => api.get('/analytics/properties/drilldown', { params }),
  postDrilldown: (payload) => api.post('/analytics/properties/drilldown', payload),
};

// Comparison API (deprecated - kept for backwards compat)
export const comparisonAPI = {
  execute: (data) => api.post('/comparisons/execute', data),
  save: (data) => api.post('/comparisons/save', data),
  list: (params) => api.get('/comparisons', { params }),
  getById: (id) => api.get(`/comparisons/${id}`),
  delete: (id) => api.delete(`/comparisons/${id}`),
  searchEntities: (params) => api.get('/comparisons/search/entities', { params }),
  getLeadFilterOptions: () => api.get('/comparisons/filter-options/leads'),
};

// OTA Management API
export const otaAPI = {
  create: (data) => api.post('/ota-listings', data),
  getAll: (params) => api.get('/ota-listings', { params }),
  getById: (id) => api.get(`/ota-listings/${id}`),
  update: (id, data) => api.put(`/ota-listings/${id}`, data),
  updateFields: (id, data) => api.put(`/ota-listings/${id}/fields`, data),
  delete: (id) => api.delete(`/ota-listings/${id}`),
  getLeadSummary: (leadId) => api.get(`/ota-listings/lead/${leadId}/summary`),
  getChecklistSLA: (id) => api.get(`/ota-listings/${id}/checklist-sla`),
  updateChecklist: (id, items) => api.put(`/ota-listings/${id}/checklist`, { items }),
  checkChecklistSLA: () => api.post('/ota-checklist-sla/check'),
  getChecklistAnalytics: () => api.get('/ota-checklist/analytics'),
};

// Timeline API
export const timelineAPI = {
  get: (entityId) => api.get(`/timeline/${entityId}`),
};

// Performance Insights API
export const performanceAPI = {
  getInsights: () => api.get('/performance/insights'),
};

// GTM Dashboard API
export const gtmDashboardAPI = {
  getData: (payload) => api.post('/gtm-dashboard', payload || {}),
  exportOTA: (params) => api.get('/gtm/export-ota', { params, responseType: 'blob' }),
};

// CX Dashboard API
export const cxDashboardAPI = {
  getClosedWonLeads: (params) => api.get('/cx/closed-won-leads', { params }),
  assignOTA: (leadId, otaIndex, data) => api.put(`/cx/leads/${leadId}/ota/${otaIndex}/assign`, data),
  completeOTA: (leadId, otaIndex, data) => api.put(`/cx/leads/${leadId}/ota/${otaIndex}/complete`, data),
  getOTAAssignments: (params) => api.get('/cx/ota-assignments', { params }),
  qcApproveOTA: (leadId, otaIndex, data) => api.put(`/cx/leads/${leadId}/ota/${otaIndex}/qc-approve`, data || {}),
  getQCPendingReviews: (params) => api.get('/qc/pending-reviews', { params }),
};

// BFR Feedback Dashboard API
export const bfrAPI = {
  getClosedWonFeedback: (params) => api.get('/bfr/closed-won-feedback', { params }),
  getOffboarding: (params) => api.get('/bfr/offboarding', { params }),
  updateFeedback: (leadId, data) => api.put(`/bfr/leads/${leadId}/feedback`, data),
  exportData: (type, params) => api.get(`/bfr/export/${type}`, { params, responseType: 'blob' }),
};


// Leads API
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  filterAdvanced: (body) => api.post('/leads/filter', body),
  approvedCount: () => api.get('/leads/stats/approved-count'),
  finalQC: (params) => api.get('/final-qc', { params }),
  duplicates: () => api.get('/leads/duplicates'),
  approveDuplicate: (id) => api.post(`/leads/${id}/approve-duplicate`),
  verify: (id) => api.post(`/leads/${id}/verify`),
  unverify: (id) => api.post(`/leads/${id}/unverify`),
  // Final-QC 5-step Admin verification (additive endpoints).
  verifyStep: (id, step) => api.post(`/leads/${id}/verify/${step}`),
  unverifyStep: (id, step) => api.post(`/leads/${id}/unverify/${step}`),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  bulkDelete: (ids) => api.post('/leads/delete-bulk', { ids }),
};

// Properties API (Separate from Leads)
export const propertiesAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  // Public endpoint for unauthenticated users (hotel onboarding portal)
  createPublic: (data) => {
    // Use axios directly without auth interceptor for public submissions
    return axios.post(`${API_URL}/properties/public`, data, {
      headers: { 'Content-Type': 'application/json' }
    });
  },
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  // Create property from CLOSED WON lead
  createFromLead: (leadId) => api.post(`/properties/from-lead/${leadId}`),
  // Get property linked to a lead
  getByLeadId: (leadId) => api.get(`/properties/by-lead/${leadId}`),
  // Complete onboarding
  completeOnboarding: (id) => api.post(`/properties/${id}/complete-onboarding`),
  // Advanced filtering
  getFilterOptions: () => api.get('/properties/filter-options'),
  filterProperties: (payload, config) => api.post('/properties/filter', payload, config),
  getRoomInventoryStats: (payload) => api.post('/properties/room-inventory-stats', payload),
};

// Property Mapping API
export const propertyMappingAPI = {
  getAll: () => api.get('/property-mappings'),
  create: (data) => api.post('/property-mappings', data),
  delete: (id) => api.delete(`/property-mappings/${id}`),
  getAvailable: () => api.get('/property-mappings/available'),
};

// Tasks API (General tasks)
export const tasksAPI = {
  getAll: () => api.get('/tasks'),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Lead Tasks API (Tasks specific to a lead)
export const leadTasksAPI = {
  getAll: (leadId) => api.get(`/leads/${leadId}/tasks`),
  create: (leadId, data) => api.post(`/leads/${leadId}/tasks`, data),
  getById: (taskId) => api.get(`/tasks/${taskId}`),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
  markComplete: (taskId) => api.put(`/tasks/${taskId}/complete`),
};

// Leads Import/Export API
export const leadsImportExportAPI = {
  previewImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importLeads: (file, mappings, skipDuplicates = true) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('skip_duplicates', skipDuplicates);
    return api.post('/leads/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  exportLeads: (format = 'csv') => api.get(`/leads/export?format=${format}`, { responseType: 'blob' }),
  exportAccount: (scope = 'simple') => api.get(`/leads/export/account?scope=${scope}`, { responseType: 'blob' }),
  downloadTemplate: () => api.get('/leads/export/template', { responseType: 'blob' }),
};

// Admin data-deletion API
export const adminDataAPI = {
  deleteData: (modules, confirm) => api.post('/admin/delete-data', { modules, confirm }),
  getDeletionLogs: () => api.get('/admin/deletion-logs'),
};

// Calls API
export const callsAPI = {
  getAll: () => api.get('/calls'),
  create: (data) => api.post('/calls', data),
};

// Visits API
export const visitsAPI = {
  getAll: () => api.get('/visits'),
  create: (data) => api.post('/visits', data),
  delete: (id) => api.delete(`/visits/${id}`),
};

// Campaigns API
export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

// Workflows API (Legacy)
export const workflowsAPI = {
  getAll: () => api.get('/workflows'),
  create: (data) => api.post('/workflows', data),
  delete: (id) => api.delete(`/workflows/${id}`),
};

// Workflows V2 API (Enhanced)
export const workflowsV2API = {
  getAll: (params) => api.get('/workflows/v2', { params }),
  getById: (id) => api.get(`/workflows/v2/${id}`),
  create: (data) => api.post('/workflows/v2', data),
  update: (id, data) => api.put(`/workflows/v2/${id}`, data),
  delete: (id) => api.delete(`/workflows/v2/${id}`),
  toggle: (id) => api.post(`/workflows/v2/${id}/toggle`),
  run: (id, testData) => api.post(`/workflows/v2/${id}/run`, testData),
  getLogs: (id, limit) => api.get(`/workflows/v2/${id}/logs`, { params: { limit } }),
  // Metadata
  getTriggers: () => api.get('/workflows/metadata/triggers'),
  getConditions: () => api.get('/workflows/metadata/conditions'),
  getVariables: () => api.get('/workflows/metadata/variables'),
};

// Message Templates API
export const templatesAPI = {
  getAll: (type) => api.get('/templates', { params: { type } }),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// Segments API
export const segmentsAPI = {
  getAll: () => api.get('/segments'),
  create: (data) => api.post('/segments', data),
  delete: (id) => api.delete(`/segments/${id}`),
};

// Field Reports API
export const fieldReportsAPI = {
  getAll: () => api.get('/field-reports'),
  create: (data) => api.post('/field-reports', data),
};

// Automations API
export const automationsAPI = {
  getAll: () => api.get('/automations'),
  create: (data) => api.post('/automations', data),
};

// Chatbots API
export const chatbotsAPI = {
  getAll: () => api.get('/chatbots'),
  create: (data) => api.post('/chatbots', data),
};

// Messages API
export const messagesAPI = {
  getAll: (leadId) => api.get('/messages', { params: { lead_id: leadId } }),
  create: (data) => api.post('/messages', data),
};

// Settings API
export const settingsAPI = {
  // Lead Stages
  getLeadStages: () => api.get('/settings/lead-stages'),
  createLeadStage: (data) => api.post('/settings/lead-stages', data),
  deleteLeadStage: (id) => api.delete(`/settings/lead-stages/${id}`),
  
  // Lead Sources
  getLeadSources: () => api.get('/settings/lead-sources'),
  createLeadSource: (data) => api.post('/settings/lead-sources', data),
  deleteLeadSource: (id) => api.delete(`/settings/lead-sources/${id}`),
  
  // Roles
  getRoles: () => api.get('/settings/roles'),
  createRole: (data) => api.post('/settings/roles', data),
  deleteRole: (id) => api.delete(`/settings/roles/${id}`),
  
  // Company Settings
  getCompanySettings: () => api.get('/settings/company'),
  updateCompanySettings: (data) => api.put('/settings/company', data),
  
  // Notification Settings
  getNotificationSettings: () => api.get('/settings/notifications'),
  updateNotificationSettings: (data) => api.put('/settings/notifications', data),
  
  // Checklist Monitor Settings
  getChecklistMonitorSettings: () => api.get('/settings/checklist-monitor'),
  updateChecklistMonitorSettings: (data) => api.put('/settings/checklist-monitor', data),

  // Section Access Control (iter221) — dynamic per-role edit rules.
  getSectionAccess: () => api.get('/settings/section-access'),
  updateSectionAccess: (rules) => api.put('/settings/section-access', { rules }),
  
  // Users
  getUsers: () => api.get('/users'),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  dismiss: (id) => api.delete(`/notifications/${id}`),
  checkOverdue: () => api.post('/notifications/check-overdue'),
  checkOTAOverdue: () => api.post('/notifications/check-ota-overdue'),
  getOverdueLeads: () => api.get('/leads/overdue-checklist'),
};

// OTA Timer API
export const otaTimerAPI = {
  startTimer: (leadId, otaIndex) => api.post(`/leads/${leadId}/ota/${otaIndex}/start-timer`),
  stopTimer: (leadId, otaIndex) => api.post(`/leads/${leadId}/ota/${otaIndex}/stop-timer`),
  submitOTA: (leadId, otaIndex, payload) => api.post(`/leads/${leadId}/ota/${otaIndex}/submit`, typeof payload === 'string' ? { listing_url: payload } : payload),
  // CX Listing Workflow — Complete / Under Review / Reject transitions.
  listingAction: (leadId, otaIndex, payload) => api.post(`/leads/${leadId}/ota/${otaIndex}/listing-action`, payload),
  getTimeLimits: () => api.get('/settings/ota-time-limits'),
  updateTimeLimits: (limits) => api.put('/settings/ota-time-limits', limits),
};

// Role Insights API (CX listings + Admin/Executive Task Command Center)
export const roleInsightsAPI = {
  cx: (params) => api.get('/role-insights/cx', { params }),
  cxLeader: (params) => api.get('/role-insights/cx-leader', { params }),
  gtmSales: (params) => api.get('/role-insights/gtm-sales', { params }),
  bfr: (params) => api.get('/role-insights/bfr', { params }),
  taskCommandCenter: (params) => api.get('/role-insights/task-command-center', { params }),
};

// Permissions API
export const permissionsAPI = {
  getMyPermissions: () => api.get('/permissions/me'),
  getRoleDefaults: () => api.get('/permissions/roles'),
  updateRoleDefaults: (role, permissions) => api.put(`/permissions/roles/${role}`, { permissions }),
  getUserPermissions: (userId) => api.get(`/permissions/users/${userId}`),
  updateUserPermissions: (userId, permissions) => api.put(`/permissions/users/${userId}`, { permissions }),
};

// Seed Data API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Hotel Lifecycle API
export const lifecycleAPI = {
  // Get all hotels with lifecycle data
  getAll: (params) => api.get('/lifecycle', { params }),
  
  // Get single hotel lifecycle data
  getById: (id) => api.get(`/lifecycle/${id}`),
  
  // Bulk delete lifecycle records
  bulkDelete: (ids) => api.post('/lifecycle/bulk-delete', { ids }),
  
  // Global sync all lifecycle records from lead data
  syncAll: () => api.post('/lifecycle/sync-all'),
  
  // Sync single lifecycle from lead data
  syncFromLead: (hotelId) => api.post(`/lifecycle/${hotelId}/sync-from-lead`),
  
  // Get users for assignment
  getUsers: () => api.get('/users'),
  
  // Start onboarding workflow
  startOnboarding: (id) => api.post(`/lifecycle/${id}/start-onboarding`),
  
  // Start offboarding workflow (Admin only)
  startOffboarding: (id, body) => api.post(`/lifecycle/${id}/start-offboarding`, body || {}),
  
  // Cancel offboarding workflow - restore to Onboarding
  cancelOffboarding: (id) => api.post(`/lifecycle/${id}/cancel-offboarding`),
  
  // Start a workflow step
  startStep: (hotelId, workflow, stepId) => 
    api.post(`/lifecycle/${hotelId}/${workflow}/steps/${stepId}/start`),
  
  // Complete a workflow step
  completeStep: (hotelId, workflow, stepId, notes) => 
    api.post(`/lifecycle/${hotelId}/${workflow}/steps/${stepId}/complete`, { notes }),
  
  // Assign user to step
  assignUser: (hotelId, workflow, stepId, userId) => 
    api.post(`/lifecycle/${hotelId}/${workflow}/steps/${stepId}/assign`, { userId }),
  
  // Get activity logs
  getActivities: (hotelId) => api.get(`/lifecycle/${hotelId}/activities`),
  
  // Final Go Live (Admin only)
  finalGoLive: (hotelId) => api.post(`/lifecycle/${hotelId}/final-go-live`),
  
  // Complete Offboarding (Admin only)
  completeOffboarding: (hotelId) => api.post(`/lifecycle/${hotelId}/complete-offboarding`),
  
  // Create lifecycle from lead
  createFromLead: (leadId) => api.post(`/lifecycle/create-from-lead/${leadId}`),
  
  // Edit completed step (Admin only)
  editStep: (hotelId, data) => api.put(`/lifecycle/${hotelId}/edit-step`, data),
  
  // Get dashboard stats
  getDashboardStats: () => api.get('/lifecycle/dashboard-stats'),
  
  // SECTION 5: Offboarding fully editable
  // Update full offboarding data
  updateOffboarding: (propertyId, data) => api.put(`/property-lifecycle/${propertyId}/offboarding-update`, data),
  
  // Update single offboarding step
  updateOffboardingStep: (propertyId, stepId, data) => 
    api.put(`/property-lifecycle/${propertyId}/offboarding-step/${stepId}`, data),

  // Phase 15 — role-gated dropdown action endpoint for offboarding steps 2-7.
  offboardingStepAction: (hotelId, stepId, body) =>
    api.post(`/lifecycle/${hotelId}/offboarding/step/${stepId}/action`, body),

  // Phase 15.5 — direct multipart NOC file upload for step 6.
  uploadNocFile: (hotelId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/lifecycle/${hotelId}/offboarding/upload-noc-file`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Phase 15 — NOC upload registration (call after file upload completes).
  uploadOffboardingNoc: (hotelId, body) =>
    api.post(`/lifecycle/${hotelId}/offboarding/upload-noc`, body),
};

// ================== BROADCAST AUTOMATION API (Sections 5-9) ==================
export const broadcastAPI = {
  // Create new broadcast
  create: (data) => api.post('/broadcast/create', data),
  
  // Get all broadcasts
  getAll: (status) => api.get('/broadcast/list', { params: { status } }),
  
  // Get single broadcast
  getById: (id) => api.get(`/broadcast/${id}`),
  
  // Update broadcast
  update: (id, data) => api.put(`/broadcast/${id}`, data),
  
  // Delete broadcast (Admin only)
  delete: (id) => api.delete(`/broadcast/${id}`),
  
  // Send broadcast immediately
  send: (id) => api.post(`/broadcast/${id}/send`),
  
  // Schedule broadcast
  schedule: (id, scheduledAt) => api.post(`/broadcast/${id}/schedule`, { scheduled_at: scheduledAt }),
  
  // Get properties for audience selection
  getProperties: (status) => api.get('/broadcast/properties/list', { params: { status } }),
};

// ================== WHATSAPP API ==================
export const whatsappAPI = {
  // Labels
  getLabels: () => api.get('/whatsapp/labels'),
  createLabel: (data) => api.post('/whatsapp/labels', data),
  updateLabel: (id, data) => api.put(`/whatsapp/labels/${id}`, data),
  deleteLabel: (id) => api.delete(`/whatsapp/labels/${id}`),
  
  // Recipients (Properties & Leads)
  getRecipients: (params) => api.get('/whatsapp/recipients', { params }),
  getRecipientFilters: () => api.get('/whatsapp/recipients/filters'),
  
  // Templates
  getTemplates: () => api.get('/whatsapp/templates'),
  getTemplate: (id) => api.get(`/whatsapp/templates/${id}`),
  createTemplate: (data) => api.post('/whatsapp/templates', data),
  updateTemplate: (id, data) => api.put(`/whatsapp/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/whatsapp/templates/${id}`),

  // Meta-approved Cloud API templates (cached in `message_templates`)
  // - status defaults to APPROVED; pass 'all' to see every status
  // - force=true forces a fresh sync regardless of 10-min cache TTL
  getMetaTemplates: (params = {}) => api.get('/whatsapp/templates/meta', {
    params: {
      status: params.status ?? 'APPROVED',
      force_refresh: params.force ? true : false,
    },
  }),
  syncMetaTemplates: () => api.post('/whatsapp/templates/sync'),
  
  // Broadcasts
  getBroadcasts: (status) => api.get('/whatsapp/broadcasts', { params: { status } }),
  getScheduledBroadcasts: () => api.get('/whatsapp/broadcasts/scheduled'),
  getBroadcastLogs: (limit) => api.get('/whatsapp/broadcasts/logs', { params: { limit } }),
  getBroadcast: (id) => api.get(`/whatsapp/broadcasts/${id}`),
  createBroadcast: (data) => api.post('/whatsapp/broadcasts', data),
  updateBroadcast: (id, data) => api.put(`/whatsapp/broadcasts/${id}`, data),
  deleteBroadcast: (id) => api.delete(`/whatsapp/broadcasts/${id}`),
  sendBroadcast: (id) => api.post(`/whatsapp/broadcasts/${id}/send`),
  scheduleBroadcast: (id, scheduledAt) => api.post(`/whatsapp/broadcasts/${id}/schedule`, { scheduled_at: scheduledAt }),
  
  // Inbox
  getInboxChats: (search) => api.get('/whatsapp/inbox', { params: { search } }),
  getChatMessages: (phone, limit) => api.get(`/whatsapp/inbox/${encodeURIComponent(phone)}/messages`, { params: { limit } }),
  sendMessage: (data) => api.post('/whatsapp/inbox/send', data),
  
  // Property/Lead Labels
  updatePropertyLabels: (propertyId, labels) => api.put(`/whatsapp/properties/${propertyId}/labels`, labels),
  updateLeadLabels: (leadId, labels) => api.put(`/whatsapp/leads/${leadId}/labels`, labels),
  
  // Settings
  getSettings: () => api.get('/whatsapp/settings'),
  updateSettings: (data) => api.put('/whatsapp/settings', data),
  toggleIntegration: (enabled) => api.post('/whatsapp/settings/toggle', { enabled }),
  getIntegrationAudit: () => api.get('/whatsapp/settings/audit'),
  // iter225 — backend workflow cutoff + per-template toggles
  getWorkflowSettings: () => api.get('/whatsapp/settings/workflows'),
  updateWorkflowSettings: (data) => api.put('/whatsapp/settings/workflows', data),
};

// CRM Modules API
export const crmModulesAPI = {
  getAll: () => api.get('/settings/modules'),
  update: (moduleId, data) => api.put(`/settings/modules/${moduleId}`, data),
  bulkUpdate: (modules) => api.put('/settings/modules/bulk-update', modules),
  checkEnabled: (moduleName) => api.get(`/settings/modules/check/${moduleName}`),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: (module) => api.get('/settings/custom-fields', { params: module ? { module } : {} }),
  create: (data) => api.post('/settings/custom-fields', data),
  update: (fieldId, data) => api.put(`/settings/custom-fields/${fieldId}`, data),
  delete: (fieldId) => api.delete(`/settings/custom-fields/${fieldId}`),
};

export default api;
